import {
  ArrowLeft, Loader, Phone, Truck, Package, MapPin, ExternalLink, Filter, X,
  User, Mail, User2, Ruler, Scale, Activity, Stethoscope, Heart, Home, Building, Edit,
  PhoneIncoming, PhoneOutgoing, CheckCircle2, CheckCircle, CreditCard
} from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ordersApi } from '../services/api';
import { formatDate, maskPhone, formatCurrency } from '../utils/helpers';
import { useTheme } from '../context/ThemeContext';
import { Modal, message } from 'antd';
import { BASE_URL as API_URL } from '../services/api';

// --- Sub-components (Moved outside to prevent re-mounting) ---

const InfoCard = ({ icon: Icon, label, value, subLabel, isDark, themeColor, colSpan = "" }) => (
  <div className={`p-4 rounded-2xl border transition-all flex items-start gap-4 min-w-[140px] flex-1 ${colSpan} ${isDark ? 'bg-white/5 border-white/5 hover:bg-white/10 shadow-inner' : 'bg-white border-gray-100 shadow-sm hover:shadow-md'}`}>
    {Icon && (
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-white/5' : ''}`} style={!isDark ? { backgroundColor: `${themeColor}15` } : {}}>
        <Icon className="w-5 h-5" style={{ color: themeColor }} />
      </div>
    )}
    <div className="min-w-0 flex-1">
      <p className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{label}</p>
      <p className={`text-sm font-black mt-1 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{value}</p>
      {subLabel && <p className={`text-[10px] font-black mt-1 ${isDark ? 'text-gray-600' : 'text-gray-500'}`}>{subLabel}</p>}
    </div>
  </div>
);

const SectionHeader = ({ title, showEdit = true, orderId, onEdit, isDark, themeColor }) => (
  <div className="flex justify-between items-center mb-8">
    <h3 className={`text-sm font-black uppercase tracking-[0.2em] ${isDark ? 'text-white' : 'text-gray-800'}`}>{title}</h3>
    {showEdit && (
      <button
        onClick={() => onEdit(`/orders/${orderId}/edit`)}
        className={`p-2 rounded-xl transition-all shadow-lg active:scale-90 ${isDark ? 'bg-white/5 text-orange-400 hover:bg-white/10' : 'bg-orange-50 text-orange-500 hover:bg-orange-100'}`}
      >
        <Edit className="w-4 h-4" />
      </button>
    )}
  </div>
);

const DispatchModal = ({
  isOpen, onClose, selectedCourier, setSelectedCourier,
  dispatchFormData, setDispatchFormData, isCreatingShipment,
  onDispatchSubmit, courierApis, isDark, themeColor
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4 backdrop-blur-sm">
      <div className={`rounded-[40px] w-full max-w-lg overflow-hidden relative shadow-2xl border transition-all ${isDark ? 'bg-[#111827] border-white/10' : 'bg-white border-gray-100'}`}>
        <div className="p-8 flex justify-between items-center border-b" style={{ background: isDark ? 'rgba(255,255,255,0.03)' : themeColor + '15', borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'transparent' }}>
          <h2 className={`text-lg font-black uppercase tracking-widest ${isDark ? 'text-white' : 'text-gray-800'}`}>Dispatch Order</h2>
          <button onClick={onClose} className={`p-1.5 rounded-full transition-all ${isDark ? 'hover:bg-white/10 text-white' : 'hover:bg-black/5 text-gray-500'}`}><X size={24} /></button>
        </div>

        <div className="p-8 space-y-8">
          <div>
            <label className={`block text-[10px] font-black uppercase tracking-widest mb-4 ${isDark ? 'text-gray-500' : 'text-gray-700'}`}>Select Courier Service</label>
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(courierApis).map(([key, courier]) => (
                <button
                  key={key}
                  onClick={() => setSelectedCourier(key)}
                  className={`p-6 rounded-[32px] border-2 flex flex-col items-center gap-2 transition-all relative group ${selectedCourier === key ? '' : 'border-transparent'}`}
                  style={{ 
                    borderColor: selectedCourier === key ? themeColor : isDark ? 'rgba(255,255,255,0.05)' : '#F3F4F6',
                    backgroundColor: selectedCourier === key ? `${themeColor}10` : isDark ? 'rgba(255,255,255,0.02)' : '#F9FAFB'
                  }}
                >
                  <span className="text-3xl filter drop-shadow-md group-hover:scale-110 transition-transform">{courier.logo}</span>
                  <span className={`text-[10px] font-black uppercase tracking-widest ${selectedCourier === key ? '' : (isDark ? 'text-gray-500' : 'text-gray-500')}`} style={selectedCourier === key ? { color: themeColor } : {}}>{courier.name}</span>
                  {key === 'delhivery' && <span className="absolute -top-2 -right-2 px-3 py-1 bg-emerald-500 text-white text-[8px] font-black uppercase tracking-widest rounded-full shadow-lg">Auto AWB</span>}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            {selectedCourier !== 'delhivery' && (
              <div>
                <label className={`block text-[10px] font-black uppercase tracking-widest mb-2 ${isDark ? 'text-gray-500' : 'text-gray-700'}`}>AWB Number <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={dispatchFormData.awbNumber}
                  onChange={(e) => setDispatchFormData(prev => ({ ...prev, awbNumber: e.target.value }))}
                  placeholder="Enter tracking ID"
                  className={`w-full px-5 py-4 border rounded-2xl text-sm font-black transition-all outline-none ${isDark ? 'bg-white/5 border-white/10 text-white focus:border-blue-500/50' : 'bg-gray-50 border-gray-200 text-gray-800 focus:border-teal-500'}`}
                />
              </div>
            )}

            <div className="grid grid-cols-1 gap-6">
              <div>
                <label className={`block text-[10px] font-black uppercase tracking-widest mb-2 ${isDark ? 'text-gray-500' : 'text-gray-700'}`}>Delivery ETA</label>
                <input
                  type="date"
                  value={dispatchFormData.expectedDeliveryDate}
                  onChange={(e) => setDispatchFormData(prev => ({ ...prev, expectedDeliveryDate: e.target.value }))}
                  className={`w-full px-5 py-4 border rounded-2xl text-sm font-black transition-all outline-none ${isDark ? 'bg-white/5 border-white/10 text-white focus:border-blue-500/50' : 'bg-gray-50 border-gray-200 text-gray-800 focus:border-teal-500'}`}
                />
              </div>
            </div>

            <div>
              <label className={`block text-[10px] font-black uppercase tracking-widest mb-2 ${isDark ? 'text-gray-500' : 'text-gray-700'}`}>Internal Remarks</label>
              <textarea
                value={dispatchFormData.remarks}
                onChange={(e) => setDispatchFormData(prev => ({ ...prev, remarks: e.target.value }))}
                placeholder="Add dispatch notes..."
                rows={2}
                className={`w-full px-5 py-4 border rounded-2xl text-sm font-black transition-all outline-none resize-none ${isDark ? 'bg-white/5 border-white/10 text-white focus:border-blue-500/50' : 'bg-gray-50 border-gray-200 text-gray-800 focus:border-teal-500'}`}
              />
            </div>
          </div>

          <button
            onClick={onDispatchSubmit}
            disabled={isCreatingShipment || !selectedCourier || (selectedCourier !== 'delhivery' && !dispatchFormData.awbNumber)}
            className={`w-full py-5 rounded-[24px] text-white font-black uppercase tracking-[0.2em] transition-all shadow-2xl active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed`}
            style={{ 
              backgroundColor: themeColor,
              boxShadow: `0 20px 40px -10px ${themeColor}40`
            }}
          >
            {isCreatingShipment ? (
              <div className="flex items-center justify-center gap-3"><Loader className="w-5 h-5 animate-spin" /> <span>Dispatching...</span></div>
            ) : (
              <div className="flex items-center justify-center gap-3"><Truck size={20} /> <span>Confirm Dispatch</span></div>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const ReasonModal = ({ reasonModal, setReasonModal, onSubmit, isDark, themeColor }) => {
  const textareaRef = React.useRef(null);

  React.useEffect(() => {
    if (reasonModal.isOpen && textareaRef.current) {
      const len = textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(len, len);
      textareaRef.current.focus();
    }
  }, [reasonModal.isOpen]);

  if (!reasonModal.isOpen) return null;

  const getThemeStyles = () => {
    switch (reasonModal.actionType) {
      case 'REJECT': return { color: '#ef4444', label: 'Rejection Reason' };
      case 'HOLD': return { color: '#6b7280', label: 'Hold Reason' };
      case 'PENDING': return { color: '#f97316', label: 'Pending Reason' };
      default: return { color: themeColor, label: 'Action Reason' };
    }
  };

  const theme = getThemeStyles();

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4 backdrop-blur-sm">
      <div className={`rounded-[40px] w-full max-w-xl overflow-hidden relative shadow-2xl border transition-all ${isDark ? 'bg-[#111827] border-white/10' : 'bg-white border-gray-100'}`}>
        <div className="p-8 flex justify-between items-center border-b" style={{ background: isDark ? 'rgba(255,255,255,0.03)' : theme.color + '15', borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'transparent' }}>
          <h3 className={`text-lg font-black uppercase tracking-widest ${isDark ? 'text-white' : 'text-gray-800'}`}>{reasonModal.title}</h3>
          <button onClick={() => !reasonModal.isLoading && setReasonModal({ ...reasonModal, isOpen: false })} className={`p-1.5 rounded-full transition-all ${isDark ? 'hover:bg-white/10 text-white' : 'hover:bg-black/5 text-gray-500'}`}><X size={24} /></button>
        </div>
        <div className="p-10">
          <label className={`block text-[10px] font-black uppercase tracking-widest mb-4 ${isDark ? 'text-gray-500' : 'text-gray-700'}`}>
            {theme.label} <span className="text-red-500">*</span>
          </label>
          <textarea
            ref={textareaRef}
            value={reasonModal.reason}
            onChange={(e) => setReasonModal({ ...reasonModal, reason: e.target.value })}
            placeholder="Please provide internal details..."
            rows={4}
            className={`w-full px-6 py-5 border rounded-[28px] text-sm font-black transition-all outline-none resize-none ${isDark ? 'bg-white/5 border-white/10 text-white focus:border-blue-500/50' : 'bg-gray-50 border-gray-200 text-gray-800 focus:border-teal-500'}`}
          />
        </div>
        <div className={`p-8 bg-black/5 border-t flex items-center justify-end gap-4 ${isDark ? 'border-white/5 bg-white/2' : 'bg-gray-50 border-gray-100'}`}>
          <button onClick={() => setReasonModal({ ...reasonModal, isOpen: false })} className={`px-8 py-3 rounded-2xl font-black uppercase tracking-widest text-[10px] transition-all hover:bg-black/5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} disabled={reasonModal.isLoading}>Cancel</button>
          <button
            onClick={onSubmit}
            disabled={reasonModal.isLoading || !reasonModal.reason.trim()}
            className={`px-10 py-4 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] transition-all shadow-xl active:scale-95 disabled:opacity-30`}
            style={{ backgroundColor: theme.color, boxShadow: `0 15px 30px -10px ${theme.color}66` }}
          >
            {reasonModal.isLoading ? <Loader className="w-4 h-4 animate-spin" /> : 'Confirm Action'}
          </button>
        </div>
      </div>
    </div>
  );
};

const SuccessModal = ({ isOpen, onClose, subMessage, isLoading, isDark, themeColor }) => (
  <Modal
    open={isOpen}
    onCancel={isLoading ? null : onClose}
    footer={null}
    centered
    closable={false}
    width={480}
    styles={{ 
      content: { 
        borderRadius: '48px', 
        padding: '0', 
        backgroundColor: isDark ? '#0B0F19' : '#FFFFFF',
        border: isDark ? '1px solid rgba(255,255,255,0.05)' : 'none',
        overflow: 'hidden'
      } 
    }}
  >
    <div className="p-12 flex flex-col items-center">
      <div className="mb-8">
        <div className={`w-28 h-28 rounded-full flex items-center justify-center transition-all ${isDark ? 'bg-white/5 shadow-2xl' : 'bg-gray-50 shadow-inner'}`} style={!isDark ? { backgroundColor: `${themeColor}10` } : {}}>
          {isLoading ? (
            <div className={`w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-2xl`}>
              <Loader className="w-10 h-10 animate-spin" style={{ color: themeColor }} />
            </div>
          ) : (
            <div className={`w-20 h-20 rounded-full flex items-center justify-center shadow-2xl`} style={{ backgroundColor: themeColor }}>
              <CheckCircle2 size={40} className="text-white" />
            </div>
          )}
        </div>
      </div>

      <h3 className={`text-3xl font-black uppercase tracking-widest mb-4 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
        {isLoading ? 'Processing...' : 'Success'}
      </h3>
      <p className={`text-center mb-12 leading-relaxed max-w-[320px] text-sm font-black uppercase tracking-widest opacity-40 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
        {isLoading ? "Synchronizing data with the global distribution network." : (subMessage || "Operation finalized and recorded in the primary registry.")}
      </p>

      <button
        onClick={onClose}
        disabled={isLoading}
        className={`w-full font-black py-5 rounded-[28px] shadow-2xl transition-all active:scale-95 text-[10px] uppercase tracking-[0.3em] ${isLoading
          ? 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'
          : 'text-white shadow-xl hover:opacity-90'
          }`}
        style={!isLoading ? { backgroundColor: themeColor, boxShadow: `0 20px 40px -10px ${themeColor}66` } : {}}
      >
        {isLoading ? 'Working...' : 'Finalize'}
      </button>
    </div>
  </Modal>
);

// Courier Configuration
const COURIER_APIS = {
  delhivery: { name: 'Delhivery', logo: '📮' }
};

function VerifiedOrderDetails() {
  const { currentTheme } = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [loadingOrderDetails, setLoadingOrderDetails] = useState(true);
  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [selectedCourier, setSelectedCourier] = useState('');
  const [dispatchFormData, setDispatchFormData] = useState({
    awbNumber: '',
    expectedDeliveryDate: '',
    remarks: ''
  });
  const [isCreatingShipment, setIsCreatingShipment] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);

  const [reasonModal, setReasonModal] = useState({
    isOpen: false,
    actionType: '',
    orderId: null,
    reason: '',
    isLoading: false,
    title: ''
  });

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successSubMessage, setSuccessSubMessage] = useState('');
  const [isModalLoading, setIsModalLoading] = useState(false);

  const [targetTab, setTargetTab] = useState('PROCESSING');
  const activeTab = location.state?.activeTab || 'PROCESSING';

  const getBackPath = () => {
    return location.state?.from || localStorage.getItem('lastPathName') || '/verified';
  };

  useEffect(() => {
    fetchOrderDetails();
  }, [id]);

  const fetchOrderDetails = async () => {
    try {
      setLoadingOrderDetails(true);
      const orderDetails = await ordersApi.getOrderDetails(id);
      setSelectedOrderDetails(orderDetails);
    } catch (error) {
      message.error('Failed to load order details');
      navigate(getBackPath());
    } finally {
      setLoadingOrderDetails(false);
    }
  };

  const handleDispatchSubmit = async () => {
    if (!selectedCourier) { message.warning('Please select a courier service'); return; }
    if (selectedCourier !== 'delhivery' && !dispatchFormData.awbNumber) { message.warning('Please enter AWB number'); return; }

    setIsCreatingShipment(true);
    const order = selectedOrderDetails?.order || {};

    try {
      let finalAwbNumber = dispatchFormData.awbNumber;
      let courierName = COURIER_APIS[selectedCourier].name;

      if (selectedCourier === 'delhivery') {
        try {
          const payload = { shipments: [{ name: order.customerName || 'Customer', add: order.address || 'Address', pin: order.areaPinCode || '560001', city: order.district || 'City', state: order.state || 'State', country: 'India', phone: order.contactNumber || '9999999999', order: order.orderId || order.id || 'ORD123', paymentMode: (String(order.paymentMode).toUpperCase().includes('COD') || ((order.grandTotal || 0) - (order.prepaidAmount || 0)) > 0) ? 'COD' : 'PREPAID', returnPin: '201301', returnCity: 'Noida', returnPhone: '7065088499', returnAdd: 'LM25 Aarogya India Pvt. Ltd.', returnState: 'Uttar Pradesh', returnCountry: 'India', productsDesc: 'Product', hsnCode: '6109', codAmount: (String(order.paymentMode).toUpperCase().includes('COD') || ((order.grandTotal || 0) - (order.prepaidAmount || 0)) > 0) ? (order.grandTotal - (order.prepaidAmount || 0)).toString() : '0', totalAmount: order.grandTotal?.toString() || '500' }], pickupLocation: { name: 'AAROGYAINDIA SURFACE' } };
          const API_BASE_URL = API_URL;
          const token = localStorage.getItem("authToken");

          const response = await fetch(`${API_BASE_URL}/shipment/create`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token || ''}` },
            body: JSON.stringify(payload)
          });

          const result = await response.json();
          if (!result.success) throw new Error(result.message || 'Shipment creation failed');
          finalAwbNumber = result.packages[0].waybill;
        } catch (error) {
          message.error(error.message);
          setIsCreatingShipment(false);
          return;
        }
      }

      const dispatchData = {
        courierName,
        trackingNumber: finalAwbNumber,
        awbNumber: finalAwbNumber,
        expectedDeliveryDate: dispatchFormData.expectedDeliveryDate || new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        remarks: dispatchFormData.remarks || `Dispatched via ${courierName}`,
        courierKey: selectedCourier
      };

      console.log('=== DISPATCH DEBUG ===', { orderId: order.id || order.orderId, order, dispatchData });

      await ordersApi.dispatchOrder(order.id || order.orderId, dispatchData);
      setSuccessSubMessage(`Order dispatched via ${courierName}. AWB: ${finalAwbNumber}`);
      setShowSuccessModal(true);
      setShowDispatchModal(false);
    } catch (error) {
      message.error(error.message);
    } finally {
      setIsCreatingShipment(false);
    }
  };

  const handleVerifyOrder = async (orderId) => {
    setIsModalLoading(true);
    setSuccessSubMessage('Order successfully verified.');
    setTargetTab('VERIFIED');
    setShowSuccessModal(true);
    try {
      await ordersApi.verifyOrder(orderId);
      setTimeout(() => setIsModalLoading(false), 500);
    } catch (error) {
      setShowSuccessModal(false);
      message.error(error.message || 'Failed to verify order');
    }
  };

  const submitReasonAction = async () => {
    const { actionType, orderId, reason } = reasonModal;
    if (!reason.trim()) { message.warning('Please enter a reason.'); return; }
    setReasonModal(prev => ({ ...prev, isLoading: true }));
    try {
      if (actionType === 'HOLD') {
        await ordersApi.holdOrder(orderId, { reason });
        setSuccessSubMessage('Order put on hold.');
        setTargetTab('ON_HOLD');
      } else if (actionType === 'REJECT') {
        await ordersApi.OrderStatus(orderId, 'REJECTED');
        setSuccessSubMessage('Order rejected.');
        setTargetTab('REJECTED');
      } else if (actionType === 'PENDING') {
        await ordersApi.markAsPending(orderId, { reason });
        setSuccessSubMessage('Order marked as pending.');
        setTargetTab('PENDING');
      }
      setReasonModal({ isOpen: false, actionType: '', orderId: null, reason: '', isLoading: false, title: '' });
      setShowSuccessModal(true);
    } catch (error) {
      message.error(`Failed to ${actionType.toLowerCase()} order`);
      setReasonModal(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleHoldOrder = (orderId) => setReasonModal({ isOpen: true, actionType: 'HOLD', orderId, reason: '', isLoading: false, title: 'Hold Order' });
  const handleRejectOrder = (orderId) => setReasonModal({ isOpen: true, actionType: 'REJECT', orderId, reason: '', isLoading: false, title: 'Reject Order' });
  const handlePendingOrder = (orderId) => setReasonModal({ isOpen: true, actionType: 'PENDING', orderId, reason: '', isLoading: false, title: 'Mark as Pending' });

  const getPageConfig = () => {
    const configs = {
      'APPROVAL_PENDING': { title: 'Processing Order', showActions: ['hold', 'pending', 'reject', 'verify'] },
      'CONFIRMED': { title: 'Verified Order', showActions: ['hold', 'pending', 'verify'] },
      'VERIFIED': { title: 'Verified Order', showActions: ['hold', 'pending', 'verify'] },
      'ON_HOLD': { title: 'Hold Order', showActions: ['pending', 'verify'] },
      'HOLD': { title: 'Hold Order', showActions: ['pending', 'verify'] },
      'REJECTED': { title: 'Rejected Order', showActions: ['pending', 'verify'] },
      'PENDING': { title: 'Pending Order', showActions: ['hold', 'reject', 'verify'] },
      'DISPATCHED': { title: 'Dispatched Order', showActions: [] },
      'DELIVERED': { title: 'Delivered Order', showActions: [] },
      'CANCELLED': { title: 'Cancelled Order', showActions: [] }
    };
    return configs[activeTab] || configs['APPROVAL_PENDING'];
  };

  const displayValue = (value) => value === null || value === undefined || value === '' || value === 0 ? '-/-' : value;

  if (loadingOrderDetails) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${currentTheme.textColor === '#FFFFFF' ? 'bg-[#030712]' : 'bg-[#F8FAFC]'}`}>
        <Loader className="w-12 h-12 animate-spin" style={{ color: currentTheme.color }} />
      </div>
    );
  }

  const order = selectedOrderDetails?.order || {};
  const lead = selectedOrderDetails?.lead || {};
  const orderItems = selectedOrderDetails?.orderItems || [];
  const pageConfig = getPageConfig();
  const isDark = currentTheme.textColor === '#FFFFFF';

  return (
    <div className={`min-h-screen p-6 font-sans transition-colors duration-500 ${isDark ? 'bg-[#0F172A]' : 'bg-[#F8FAFC]'}`}>
      
      {showDispatchModal && (
        <DispatchModal isOpen={showDispatchModal} onClose={() => setShowDispatchModal(false)} selectedCourier={selectedCourier} setSelectedCourier={setSelectedCourier} dispatchFormData={dispatchFormData} setDispatchFormData={setDispatchFormData} isCreatingShipment={isCreatingShipment} onDispatchSubmit={handleDispatchSubmit} courierApis={COURIER_APIS} isDark={isDark} themeColor={currentTheme.color} />
      )}
      <ReasonModal reasonModal={reasonModal} setReasonModal={setReasonModal} onSubmit={submitReasonAction} isDark={isDark} themeColor={currentTheme.color} />
      <SuccessModal isOpen={showSuccessModal} onClose={() => { if (!isModalLoading) { setShowSuccessModal(false); navigate(getBackPath(), { state: { activeTab: targetTab } }); } }} subMessage={successSubMessage} isLoading={isModalLoading} isDark={isDark} themeColor={currentTheme.color} />

      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(getBackPath())} className={`p-3 rounded-2xl transition-all shadow-lg active:scale-90 ${isDark ? 'bg-white/5 text-white hover:bg-white/10' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
            <ArrowLeft size={24} style={{ color: currentTheme.color }} />
          </button>
          <div>
            <h1 className={`text-2xl font-black uppercase tracking-[0.2em] ${isDark ? 'text-white' : 'text-gray-800'}`}>{pageConfig.title}</h1>
            <p className={`text-[10px] font-black uppercase tracking-widest mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Workflow Context</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(`/orders/${order.id}/edit`)} className={`p-3 rounded-2xl transition-all shadow-lg active:scale-90 ${isDark ? 'bg-white/5 text-orange-400 hover:bg-white/10' : 'bg-orange-50 text-orange-500 hover:bg-orange-100'}`}>
            <Edit className="w-6 h-6" />
          </button>
          <div className="px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-[0.2em] shadow-xl text-white" style={{ backgroundColor: currentTheme.color }}>
            {order.status || 'Verified'}
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-8 max-w-[1600px] mx-auto">
        <div className="flex-1 space-y-6">
          {/* Order Summary Ribbon */}
          <div className={`rounded-[40px] overflow-hidden flex items-stretch shadow-2xl border transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
            <div className="flex-1 grid grid-cols-3 p-8 gap-8">
              <div>
                <p className={`text-[10px] font-black uppercase tracking-widest mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Order ID</p>
                <p className={`text-xl font-black ${isDark ? 'text-white' : 'text-gray-900'}`}>{displayValue(order.orderId)}</p>
              </div>
              <div className={`border-l pl-8 ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                <p className={`text-[10px] font-black uppercase tracking-widest mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Order Date</p>
                <p className={`text-lg font-black ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{formatDate(order.orderDate)}</p>
              </div>
              <div className={`border-l pl-8 ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                <p className={`text-[10px] font-black uppercase tracking-widest mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Primary Product</p>
                <p className={`text-lg font-black truncate ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{orderItems.length > 0 ? orderItems[0].productName : 'Product'}</p>
              </div>
            </div>
            <div className="px-10 flex flex-col justify-center items-center text-white min-w-[200px]" style={{ backgroundColor: currentTheme.color }}>
              <span className="text-[10px] font-black uppercase tracking-[0.2em] opacity-70 mb-1">Payable Amount</span>
              <span className="text-3xl font-black">{formatCurrency(Math.max(0, (order.grandTotal || 0) - (order.prepaidAmount || 0)))}</span>
            </div>
          </div>

          <div className={`p-8 rounded-[40px] shadow-xl border transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
            <SectionHeader title="Customer Details" orderId={order.id} onEdit={navigate} isDark={isDark} themeColor={currentTheme.color} />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <InfoCard icon={User} label="Customer name" value={displayValue(order.customerName || lead.customerName)} isDark={isDark} themeColor={currentTheme.color} />
              <InfoCard icon={Phone} label="Contact number" value={displayValue(order.contactNumber || lead.contactNumber)} isDark={isDark} themeColor={currentTheme.color} />
              <InfoCard icon={Phone} label="Alternate number" value={displayValue(order.alternateNumber || lead.alternateNumber)} isDark={isDark} themeColor={currentTheme.color} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <InfoCard icon={Mail} label="Email Address" value={displayValue(order.email || lead.email)} isDark={isDark} themeColor={currentTheme.color} />
              </div>
              <InfoCard icon={User2} label="Gender" value={displayValue(order.gender || lead.gender)} isDark={isDark} themeColor={currentTheme.color} />
              <InfoCard icon={Ruler} label="Height / Weight" value={`${displayValue(order.height || lead.height)} / ${displayValue(order.weight || lead.weight)}`} isDark={isDark} themeColor={currentTheme.color} />
            </div>
          </div>

          <div className={`p-8 rounded-[40px] shadow-xl border transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
            <SectionHeader title="Medical Profile" orderId={order.id} onEdit={navigate} isDark={isDark} themeColor={currentTheme.color} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InfoCard icon={Activity} label="Primary Problem" value={displayValue(order.actualProblem || lead.actualProblem)} isDark={isDark} themeColor={currentTheme.color} />
              <InfoCard icon={Heart} label="Marital Status" value={displayValue(order.maritalStatus || lead.maritalStatus)} isDark={isDark} themeColor={currentTheme.color} />
              <div className="md:col-span-2">
                <InfoCard icon={Stethoscope} label="Existing Medical Treatment" value={displayValue(order.existingMedicalTreatment || lead.existingMedicalTreatment)} isDark={isDark} themeColor={currentTheme.color} />
              </div>
            </div>
          </div>

          <div className={`p-8 rounded-[40px] shadow-xl border transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
            <SectionHeader title="Shipping Address" orderId={order.id} onEdit={navigate} isDark={isDark} themeColor={currentTheme.color} />
            <div className={`p-6 rounded-[28px] mb-8 flex items-start gap-4 border transition-all ${isDark ? 'bg-white/5 border-white/5 text-gray-300' : 'bg-gray-50 border-gray-100 text-gray-700'}`}>
              <div className={`p-3 rounded-2xl flex-shrink-0 ${isDark ? 'bg-white/5' : 'bg-white shadow-sm'}`}>
                <Home className="w-6 h-6" style={{ color: currentTheme.color }} />
              </div>
              <span className="text-lg font-black tracking-wide leading-relaxed">{displayValue(order.address || lead.address)}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 px-2">
               {[
                 { label: 'Pin Code', value: displayValue(order.areaPinCode || lead.areaPinCode) },
                 { label: 'City/Town', value: displayValue(order.cityTown || lead.cityTown || lead.city || order.city) },
                 { label: 'District', value: displayValue(order.district || lead.district) },
                 { label: 'State', value: displayValue(order.state || lead.state) }
               ].map((item, idx) => (
                 <div key={idx}>
                    <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{item.label}</p>
                    <p className={`text-sm font-black ${isDark ? 'text-gray-300' : 'text-gray-800'}`}>{item.value}</p>
                 </div>
               ))}
            </div>
          </div>
        </div>

        <div className="w-full lg:w-[420px] space-y-6">
          <div className={`p-8 rounded-[40px] shadow-xl border transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
            <h3 className={`text-sm font-black uppercase tracking-[0.2em] mb-8 ${isDark ? 'text-white' : 'text-gray-800'}`}>Logistics & Workflow</h3>
            <div className="grid grid-cols-2 gap-4 mb-8">
              {[
                { label: 'Order Type', value: order.orderType, icon: Package, color: 'text-blue-400' },
                { label: 'Shipping', value: order.shippingMethod, icon: Truck, color: 'text-purple-400' },
                { label: 'Payment Type', value: order.paymentType, icon: CreditCard, color: 'text-orange-400' },
                { label: 'Payment Mode', value: order.paymentMode, icon: ExternalLink, color: 'text-emerald-400' }
              ].map((item, idx) => (
                <div key={idx} className={`p-4 rounded-2xl border transition-all ${isDark ? 'bg-white/2 border-white/5' : 'bg-gray-50 border-gray-100'}`}>
                  <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{item.label}</p>
                  <p className={`text-xs font-black truncate ${item.color || (isDark ? 'text-gray-300' : 'text-gray-800')}`}>{displayValue(item.value)}</p>
                </div>
              ))}
            </div>
            <div className="space-y-3">
              {pageConfig.showActions.map(action => {
                const cfg = {
                  hold: { onClick: () => handleHoldOrder(order.id), className: `bg-gray-500/10 text-gray-500 border-gray-500/20`, label: "Hold Order" },
                  pending: { onClick: () => handlePendingOrder(order.id), className: `bg-orange-500/10 text-orange-500 border-orange-500/20`, label: "Mark Pending" },
                  reject: { onClick: () => handleRejectOrder(order.id), className: `bg-red-500/10 text-red-500 border-red-500/20`, label: "Reject Order" },
                  verify: { onClick: () => handleVerifyOrder(order.id), className: "text-white shadow-xl", style: { backgroundColor: currentTheme.color }, label: "Verify & Lock" },
                  dispatch: { onClick: () => setShowDispatchModal(true), className: "bg-blue-500 text-white shadow-xl", label: "Process Dispatch" }
                }[action];
                if (!cfg) return null;
                return (
                  <button key={action} onClick={cfg.onClick} style={cfg.style} className={`w-full py-4 rounded-2xl font-black uppercase tracking-widest text-[10px] transition-all active:scale-95 border border-transparent shadow-sm hover:shadow-lg ${cfg.className}`}>{cfg.label}</button>
                );
              })}
            </div>
          </div>

          <div className={`p-8 rounded-[40px] shadow-2xl border transition-all ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100'}`}>
             <h3 className={`text-sm font-black uppercase tracking-[0.2em] mb-8 ${isDark ? 'text-white' : 'text-gray-800'}`}>Financial Matrix</h3>
             <div className="space-y-4">
                {[
                  { label: 'Sub Total', value: formatCurrency(order.subTotal || order.grandTotal || 0), color: isDark ? 'text-gray-200' : 'text-gray-900' },
                  { label: 'Prepaid amount', value: formatCurrency(order.prepaidAmount || 0), color: 'text-emerald-500' },
                  { label: 'Total Discounts', value: formatCurrency(order.discount || order.totalDiscounts || 0), color: 'text-emerald-500' },
                  { label: 'Shipping Charges', value: (order.shippingCharges === 0 || !order.shippingCharges) ? 'Free' : formatCurrency(order.shippingCharges), color: 'text-emerald-500' }
                ].map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center">
                    <span className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{item.label}</span>
                    <span className={`text-sm font-black ${item.color}`}>{item.value}</span>
                  </div>
                ))}
                <div className="pt-8 border-t border-dashed mt-6" style={{ borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}>
                  <div className="flex justify-between items-center">
                    <span className={`text-xs font-black uppercase tracking-[0.3em] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Net Payable:</span>
                    <span className="text-3xl font-black" style={{ color: currentTheme.color }}>{formatCurrency(Math.max(0, (order.grandTotal || 0) - (order.prepaidAmount || 0)))}</span>
                  </div>
                </div>
             </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VerifiedOrderDetails;